import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from '@wlb/shared/context/AuthContext';
import { AdminSessionProvider } from '@wlb/shared/context/AdminSessionContext';
import { ThemeProvider } from '@wlb/shared/theme';
import { AdminShell } from './components/layout/AdminShell';
import { ThemeApplier } from './components/layout/ThemeApplier';
import { renderAppErrorFallback } from './components/feedback/AppErrorFallback';
import { SessionExpiredModal } from './components/feedback/SessionExpiredModal';
import { Toast } from './components/feedback/Toast';
import { ErrorBoundary } from './ErrorBoundary';
import { AgentEditTab } from './features/agents/AgentEditTab';
import { AgentProfileTab } from './features/agents/AgentProfileTab';
import { AdminLogin } from './features/auth/AdminLogin';
import { AnonymousOnly, RequireAdmin } from './features/auth/RequireAdmin';
import { UserActivationTab } from './features/users/UserActivationTab';
import { UserLoginsTab } from './features/users/UserLoginsTab';
import { UserPostsTab } from './features/users/UserPostsTab';
import { UserProfileTab } from './features/users/UserProfileTab';
import { AdminAccounts } from './pages/AdminAccounts';
import { AgentDetails } from './pages/AgentDetails';
import { Agents } from './pages/Agents';
import { Announcements } from './pages/Announcements';
import { Dashboard } from './pages/Dashboard';
import { ErrorLogs } from './pages/ErrorLogs';
import { Groups } from './pages/Groups';
import { Affiliates } from './pages/Affiliates';
import { Moderation } from './pages/Moderation';
import { Posts } from './pages/Posts';
import { Settings } from './pages/Settings';
import { Statistics } from './pages/Statistics';
import { UserDetails } from './pages/UserDetails';
import { Users } from './pages/Users';

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AdminSessionProvider>
          <BrowserRouter>
            <ThemeApplier />
            <ErrorBoundary fallback={renderAppErrorFallback}>
              <Routes>
                <Route
                  path="/login"
                  element={
                    <AnonymousOnly>
                      <AdminLogin />
                    </AnonymousOnly>
                  }
                />
                {/* Every panel route sits inside RequireAdmin, so deep links to
                    a child route are guarded exactly like their parent. */}
                <Route element={<RequireAdmin />}>
                  <Route element={<AdminShell />}>
                    <Route index element={<Dashboard />} />
                    <Route path="statistics" element={<Statistics />} />
                    <Route path="users" element={<Users />} />
                    {/* Each member sub-module is its own URL under the record's
                        layout route: the record is fetched once by the parent,
                        so switching sub-module swaps only the panel and a deep
                        link still lands on the right tab. */}
                    <Route path="users/:userGuid" element={<UserDetails />}>
                      <Route index element={<UserProfileTab />} />
                      <Route path="posts" element={<UserPostsTab />} />
                      <Route path="logins" element={<UserLoginsTab />} />
                      <Route path="activation" element={<UserActivationTab />} />
                    </Route>
                    <Route path="agents" element={<Agents />} />
                    <Route path="agents/:agentId" element={<AgentDetails />}>
                      <Route index element={<AgentProfileTab />} />
                      <Route path="edit" element={<AgentEditTab />} />
                    </Route>
                    <Route path="posts" element={<Posts />} />
                    <Route path="groups" element={<Groups />} />
                    <Route path="affiliates" element={<Affiliates />} />
                    <Route
                      path="moderation"
                      element={<Navigate to="/moderation/pending" replace />}
                    />
                    <Route
                      path="moderation/pending"
                      element={<Moderation queue="pending" />}
                    />
                    <Route
                      path="moderation/reviewed"
                      element={<Moderation queue="reviewed" />}
                    />
                    <Route path="error-logs" element={<ErrorLogs />} />
                    <Route path="admin-accounts" element={<AdminAccounts />} />
                    <Route path="announcements" element={<Announcements />} />
                    <Route path="settings" element={<Settings />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Route>
                </Route>
              </Routes>
              <SessionExpiredModal />
              <Toast />
            </ErrorBoundary>
          </BrowserRouter>
        </AdminSessionProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
